import { useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useNimbuxHotelsList } from '@frontend/features/hotel-content/hooks/useNimbuxHotelsList';
import {
  LoadingState,
  ErrorState,
  LoadingIndicator,
  EmptyMessage,
} from '@frontend/features/hotel-content-manager/components/NimbuxHotelsList/';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { NimbuxHotelListFilters } from '@frontend/features/hotel-content-manager/components/NimbuxHotelListFilters';
import { NimbuxContentHotelCard } from '@frontend/features/hotel-content-manager/components/NimbuxContentHotelCard';
import { routes } from '../routes';

export const NimbuxHotelsList = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [searchByName, setSearchByName] = useDebouncedState('', 500);
  const [searchByAddress, setSearchByAddress] = useDebouncedState('', 500);
  const [searchByDistance, setSearchByDistance] = useDebouncedState(2, 500);
  const [orderBy, setOrderBy] = useState('name');
  const [selectedCountry, setSelectedCountry] = useState('');
  const {
    allHotels,
    isLoading,
    isError,
    isFetchingNextPage,
    hasNextPage,
    observerTarget,
  } = useNimbuxHotelsList({
    pageSize: 10,
    name: searchByName,
    country: selectedCountry,
    address: searchByAddress,
    distance: searchByDistance,
    sort: orderBy,
  });

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError) {
    return <ErrorState />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          paddingLeft: theme.spacing(2),
          paddingY: theme.spacing(2),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="h6"
          color="text.primary"
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
            letterSpacing: theme.typography.h6.letterSpacing,
          }}
        >
          {t('hotelContent.nimbuxHotels.title')}
        </Typography>
      </Box>

      <Box
        sx={{
          padding: theme.spacing(3),
        }}
        display="flex"
        gap={theme.spacing(3)}
      >
        <Box
          sx={{
            position: 'sticky',
            top: theme.spacing(0.5),
            alignSelf: 'flex-start',
            zIndex: 10,

          }}
        >
          <NimbuxHotelListFilters
            search={searchByName}
            onSearchChange={setSearchByName}
            orderBy={orderBy}
            onOrderByChange={setOrderBy}
            selectedCountry={selectedCountry}
            onSelectedCountryChange={setSelectedCountry}
            address={searchByAddress}
            onAddressChange={setSearchByAddress}
            distance={searchByDistance}
            onDistanceChange={setSearchByDistance}
          />

        </Box>

        <Box
          display="flex"
          flexDirection="column"
          gap={theme.spacing(5)}
          flex={1}
        >
          {allHotels.map((hotel, index) => (
            <NimbuxContentHotelCard
              key={hotel.id || `hotel-${index}`}
              data={hotel}
              editRoute={routes.dashboard.hotelContentView.root.replace(':hotelId', hotel.id!.toString())}
            />
          ))}
          <Box
            ref={observerTarget}
            sx={{
              height: 20,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {isFetchingNextPage && <LoadingIndicator />}
          </Box>
          {!hasNextPage && allHotels.length > 0 && <EmptyMessage />}
        </Box>
      </Box>
    </Box>
  );
};
